import {
  AlertTriangle,
  BarChart3,
  Code,
  Eye,
  FileCheck,
  GitBranch,
  Lock,
  Scale,
  Shield,
} from "lucide-react";

const valueCards = [
  {
    icon: FileCheck,
    title: "License Risk Intelligence",
    description:
      "Clear visibility into obligations, restrictions, and compliance risks across every dependency.",
  },
  {
    icon: Shield,
    title: "Security & Vulnerability Insights",
    description:
      "Identify and prioritize vulnerabilities with actionable clarity, not just noise.",
  },
  {
    icon: AlertTriangle,
    title: "Operational & Technical Health",
    description:
      "Detect outdated, deprecated, and high-risk dependencies before they become problems.",
  },
  {
    icon: Lock,
    title: "Encryption & PQC Readiness",
    description:
      "Understand cryptographic usage and prepare your codebase for post-quantum risks.",
  },
];

const steps = [
  {
    number: "01",
    icon: GitBranch,
    title: "Connect Repositories",
    description: "Link your source code repositories in seconds.",
  },
  {
    number: "02",
    icon: BarChart3,
    title: "Analyze & Enrich Data",
    description: "ORbiT8 scans, enriches, and scores every component.",
  },
  {
    number: "03",
    icon: Eye,
    title: "Visualize & Act",
    description: "Get a unified risk view and take targeted action.",
  },
];

const personas = [
  {
    icon: Code,
    title: "Engineering",
    description: "Manage dependencies and improve system health with clear, actionable insights.",
  },
  {
    icon: Shield,
    title: "Security",
    description: "Identify vulnerabilities and reduce exposure across your entire open-source stack.",
  },
  {
    icon: Scale,
    title: "Legal",
    description: "Ensure license compliance and governance without slowing down delivery.",
  },
];

const regulatoryItems = [
  {
    title: "SBOM Transparency",
    description: "Generate and maintain accurate software bills of materials.",
  },
  {
    title: "Cyber Resilience Act",
    description: "Align with CRA requirements for software supply chain security.",
  },
  {
    title: "Open Source Governance",
    description: "Establish clear policies and track compliance across teams.",
  },
];

const Features = () => {
  return (
    <>
      <section id="features" className="relative z-10 px-6 py-24">
        <div className="container mx-auto">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-[-0.01em] text-foreground md:text-4xl">
              Every Dimension of Risk, <span className="text-primary">Covered</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl font-light leading-relaxed text-muted-foreground">
              From license obligations to post-quantum cryptography — nothing slips through the cracks.
            </p>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {valueCards.map((card) => (
              <article
                key={card.title}
                className="group rounded-xl border border-border bg-card/80 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/15">
                  <card.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-muted-foreground">
                  {card.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="relative z-10 px-6 py-24">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-[-0.01em] text-foreground md:text-4xl">
              How It Works
            </h2>
            <p className="mt-4 font-light text-muted-foreground">
              Three steps to complete open-source risk clarity.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {steps.map((step, index) => (
              <article key={step.title} className="group relative text-center">
                {index < steps.length - 1 && (
                  <div className="absolute left-[60%] top-12 hidden h-px w-[80%] bg-border md:block" />
                )}
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-card/80 backdrop-blur-sm transition-colors group-hover:border-primary/30">
                  <step.icon className="h-7 w-7 text-primary" />
                </div>
                <span className="text-xs font-bold tracking-wider text-primary">{step.number}</span>
                <h3 className="mt-2 text-lg font-semibold text-foreground">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm font-light text-muted-foreground">
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="teams" className="relative z-10 px-6 py-24">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-[-0.01em] text-foreground md:text-4xl">
              Built for Cross-Functional Teams
            </h2>
            <p className="mt-4 font-light text-muted-foreground">
              One platform, shared clarity, from engineering to the boardroom.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {personas.map((persona) => (
              <article
                key={persona.title}
                className="rounded-xl border border-border bg-card/80 p-8 text-center backdrop-blur-sm transition-all duration-300 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                  <persona.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  {persona.title}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-muted-foreground">
                  {persona.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 px-6 py-24">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold tracking-[-0.01em] text-foreground md:text-4xl">
            Designed for a Changing Regulatory Landscape
          </h2>
          <p className="mx-auto mt-6 max-w-2xl font-light leading-relaxed text-muted-foreground">
            Rising expectations around open-source transparency don't have to mean rising
            complexity. Stay prepared, effortlessly.
          </p>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {regulatoryItems.map((item) => (
              <article
                key={item.title}
                className="rounded-xl border border-border bg-card/80 p-6 backdrop-blur-sm transition-all hover:border-primary/30"
              >
                <h3 className="font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm font-light text-muted-foreground">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Features;
